import React,{useEffect,useMemo,useRef,useState}from'react';
import api from'../api';

const CATS=['Customer','Product','Invoice','Credit','POS','Return','Setting', 'Auth', 'User'];

// ---- Auto refresh presets (seconds) ----
const REFRESH_PRESETS=[
  {label:'Off', value:0},
  {label:'10s',value:10},
  {label:'30s',value:30},
  {label:'60s',value:60},
  {label:'5m', value:300},
];
const REFRESH_KEY='logging:autoRefresh';

export default function Logging(){
  const [from,setFrom]=useState('');
  const [to,setTo]=useState('');
  const [q,setQ]=useState('');
  const [cats,setCats]=useState(new Set(CATS));
  const [page,setPage]=useState(1);
  const [pageSize,setPageSize]=useState(25);
  const [data,setData]=useState({page:1,pageSize:25,total:0,rows:[]});
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [infoOpen, setInfoOpen] = useState(null);

  // ---- Auto refresh state ----
  const [autoRefreshSec,setAutoRefreshSec]=useState(()=>{
    const v=Number(localStorage.getItem(REFRESH_KEY)||'0');
    return Number.isFinite(v)?v:0;
  });
  const [autoOn,setAutoOn]=useState(()=> (Number(localStorage.getItem(REFRESH_KEY)||'0')>0));

  function toggle(cat){
    setCats(prev=>{const n=new Set(prev); if(n.has(cat)) n.delete(cat); else n.add(cat); return n;});
    setPage(1);
  }
  function selectAll(){ setCats(new Set(CATS)); setPage(1); }
  function clearAll(){ setCats(new Set()); setPage(1); }
  function clearFilters(){ setFrom(''); setTo(''); setQ(''); selectAll(); setPage(1); }

  const qs=useMemo(()=>{
    const p=new URLSearchParams();
    if(from) p.set('from',from);
    if(to) p.set('to',to);
    const list=CATS.filter(c=>cats.has(c));
    if(list.length && list.length!==CATS.length) p.set('categories',list.join(','));
    if(q.trim()) p.set('q',q.trim());
    p.set('page',String(page));
    p.set('pageSize',String(pageSize));
    return p.toString();
  },[from,to,cats,q,page,pageSize]);

  // ---- Single fetch function (used by effect + timer) ----
  const inFlight=useRef(false);
  const lastQsRef=useRef(qs);
  async function loadLogs(currentQs=qs){
    if(inFlight.current) return;
    inFlight.current=true;
    setLoading(true); setError('');
    try{
      const resp=await api.get('/logs?'+currentQs);
      setData(resp.data);
      lastQsRef.current=currentQs;
    }catch(e){ setError('Failed to load'); }
    finally{ setLoading(false); inFlight.current=false; }
  }

  // Initial/whenever filters change
  useEffect(()=>{ let cancel=false; (async()=>{ if(!cancel) await loadLogs(qs); })(); return()=>{cancel=true}; },[qs]);

  // Persist auto setting
  useEffect(()=>{ localStorage.setItem(REFRESH_KEY,String(autoOn?autoRefreshSec:0)); },[autoOn,autoRefreshSec]);

  // Visibility-aware auto refresh
  useEffect(()=>{
    let id=null;
    const tick=()=>{ if(document.visibilityState==='visible' && autoOn && autoRefreshSec>0) loadLogs(lastQsRef.current); };
    if(autoOn && autoRefreshSec>0){ id=setInterval(tick, autoRefreshSec*1000); }
    const onVis=()=>{ if(document.visibilityState==='visible' && autoOn && autoRefreshSec>0) loadLogs(lastQsRef.current); };
    document.addEventListener('visibilitychange', onVis);
    return()=>{ if(id) clearInterval(id); document.removeEventListener('visibilitychange', onVis); }
  },[autoOn,autoRefreshSec]);

  const pageCount=Math.max(1,Math.ceil((data.total||0)/pageSize));
  const rows=data.rows||[];

  const busy=useRef(false);
  async function downloadCSV(){
    if(busy.current) return;
    busy.current=true;
    try{
      const p=new URLSearchParams(qs); p.set('page','1'); p.set('pageSize','100000');
      const resp=await api.get('/logs?'+p.toString());
      const j=resp.data||{rows:[]};
      const arr=[['Time','User','Field','Action','ID','Details'],...j.rows.map(r=>[
        fmtDate(r.at), r.user, r.category, r.action, r.entityId||'', r.details
      ])];
      const csv=arr.map(row=>row.map(cell=>{
        const s=String(cell??''); const need=/[",\n\r]/.test(s); const esc=s.replace(/"/g,'""'); return need?`"${esc}"`:esc;
      }).join(',')).join('\n');
      const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'});
      const url=URL.createObjectURL(blob); const a=document.createElement('a');
      a.href=url; a.download=`logs_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
    }catch(e){ console.error(e); }
    finally{ busy.current=false; }
  }

  function fmtDate(x){try{return new Date(x).toLocaleString()}catch(e){return x}}

  // --------- UI ---------
  return (<div className='container'>

    {/* Top bar: title left, date + auto-refresh + refresh-now right */}
    <div className='toolbar'
      style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
      <div style={{display:'flex',gap:8,alignItems:'center',marginRight:'auto'}}>
        <h2 style={{margin:0}}>Logging</h2>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('logging')}
          style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Logging"
        >
          ℹ️
        </button>
      </div>

      <div className='flex' style={{gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <label style={{fontSize:12}}>From</label>
        <input type='date' value={from} onChange={e=>{setFrom(e.target.value); setPage(1);}}/>
        <label style={{fontSize:12}}>To</label>
        <input type='date' value={to} onChange={e=>{setTo(e.target.value); setPage(1);}}/>

        <div className='flex' style={{gap:6,alignItems:'center'}}>
          <label className='flex' style={{alignItems:'center',gap:6}}>
            <input type='checkbox' checked={autoOn} onChange={e=>setAutoOn(e.target.checked)}/>
            <span>Auto-refresh</span>
          </label>
          <select value={autoRefreshSec}
                  onChange={e=>{const v=Number(e.target.value); setAutoRefreshSec(v); if(v===0) setAutoOn(false); else setAutoOn(true);}}
                  disabled={!autoOn}>
            {REFRESH_PRESETS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className='btn secondary' onClick={()=>loadLogs(lastQsRef.current)}>Refresh now</button>
        </div>
      </div>
    </div>
    {/* Info Modal */}
    {infoOpen === 'logging' && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}
        onClick={() => setInfoOpen(null)}
      >
        <div
          className="card"
          style={{ maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', padding: 24, borderRadius: 8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Logging</div>
            <button
              className="btn outline small"
              onClick={() => setInfoOpen(null)}
              style={{ padding: '2px 6px', fontSize: '12px' }}
            >
              ✕
            </button>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg,#0b1220)' }}>
            <p><strong>Logging</strong> shows system events grouped by categories like Customer, Product, Invoice, POS and more. Use the filters to narrow results by date, category, or free-text search.</p>
            <p><strong>Key features:</strong></p>
            <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
              <li><strong>Auto-refresh:</strong> Enable periodic reloads to watch incoming events in real time.</li>
              <li><strong>Filters:</strong> Date range, categories and text search help find relevant logs.</li>
              <li><strong>CSV export:</strong> Download filtered logs as CSV for auditing and analysis.</li>
              <li><strong>Categories:</strong> Toggle specific categories using the checkboxes below the actions.</li>
            </ul>
            <p><strong>Notes:</strong> Logs may contain sensitive information; treat exported CSVs accordingly and restrict access to users with proper permissions.</p>
          </div>
        </div>
      </div>
    )}

    {/* Actions row: buttons left, search+csv right */}
    <div className='card' style={{padding:'10px 12px', marginBottom:10}}>
      <div className='flex' style={{gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <div className='flex' style={{gap:8}}>
          <button className='btn secondary' onClick={selectAll}>Select all</button>
          <button className='btn secondary' onClick={clearAll}>Clear</button>
          <button className='btn secondary' onClick={clearFilters}>Reset</button>
        </div>

        <div className='right flex' style={{marginLeft:'auto', gap:8, minWidth:280}}>
          <input className='input' style={{flex:1,minWidth:240}}
                 placeholder='Filter logs by user, ID, action or details…'
                 value={q} onChange={e=>{setQ(e.target.value); setPage(1);}}/>
          <button className='btn secondary' onClick={downloadCSV}>CSV</button>
        </div>
      </div>

      {/* Categories */}
      <div className='grid' style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8, marginTop:10}}>
        {CATS.map(cat=>(
          <label key={cat} className='flex' style={{justifyContent:'flex-start',alignItems:'center',gap:8}}>
            <input type='checkbox' checked={cats.has(cat)} onChange={()=>toggle(cat)}/>
            <span>{cat}</span>
          </label>
        ))}
      </div>
    </div>

    {/* Table + pager */}
    <div className='card'>
      <div className='flex' style={{alignItems:'flex-start',gap:10,flexWrap:'wrap'}}>
        <div className='flex' style={{alignItems:'center',gap:8,flexWrap:'wrap'}}>
          {!loading && !error && <div className='muted'>Showing page {data.page||1} of {pageCount} - Total {data.total||0}</div>}
          {loading && <div className='muted'>Loading...</div>}
          {error && <div style={{color:'var(--danger)'}}>{error}</div>}
          {autoOn && autoRefreshSec>0 &&
            <span className='muted' style={{padding:'2px 8px', border:'1px solid var(--border)', borderRadius:999, fontSize:12, whiteSpace:'nowrap'}}>
              Auto: {REFRESH_PRESETS.find(p=>p.value===autoRefreshSec)?.label || `${autoRefreshSec}s`}
            </span>
          }
        </div>

        <div className='flex' style={{alignItems:'center',gap:12,flexWrap:'wrap',width:'100%',justifyContent:'space-between'}}>
          <div className='flex' style={{alignItems:'center',gap:8}}>
            <span className='muted'>Rows/ page</span>
            <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value)); setPage(1);}}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className='flex' style={{alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}}>
            <button className='btn secondary' onClick={()=>setPage(1)} disabled={page<=1}>{'<<'}</button>
            <button className='btn secondary' onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1}>{'<'}</button>
            <span className='muted' style={{padding:'0 6px'}}>Page <b>{page}</b> / {pageCount}</span>
            <button className='btn secondary' onClick={()=>setPage(p=>p+1)} disabled={page>=pageCount}>{'>'}</button>
            <button className='btn secondary' onClick={()=>setPage(pageCount)} disabled={page>=pageCount}>{'>>'}</button>
          </div>
        </div>
      </div>
      <div style={{overflowX:'auto',marginTop:10}}>
        <table className='table'>
          <thead><tr>
            <th>Time</th><th>User</th><th>Field</th><th>Action</th><th>ID</th><th>Details</th>
          </tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id}>
                <td style={{whiteSpace:'nowrap'}}>{fmtDate(r.at)}</td>
                <td>{r.user}</td>
                <td>{r.category}</td>
                <td><span className='mono'>{r.action}</span></td>
                <td>{r.entityId||'—'}</td>
                <td style={{minWidth:320}}>{r.details}</td>
              </tr>
            ))}
            {!loading && !error && rows.length===0 && (
              <tr><td colSpan={6} style={{textAlign:'center',padding:24}}>No logs match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>);
}
